import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  supportApi,
  type AssigneeType,
  type ChatwootMessage,
  type Conversation,
} from '@/api/endpoints/support';
import { socketClient } from '@/api/socket';

// O tempo real vem do Socket.IO no NotificationProvider global, que invalida
// estes caches nos eventos. O polling abaixo é só REDE DE SEGURANÇA (caso o
// websocket caia), por isso os intervalos são folgados.
//
// As mutations recebem o id da conversa NA CHAMADA, não no hook: trocar de
// conversa no meio de um envio não pode mandar nada (nem invalidar nada) na
// conversa errada. Foi assim que o "Assumir" do pop-up pegou a conversa que
// estava aberta na tela em vez da do pop-up.
const CONVERSATIONS_POLL_MS = 30000;
const MESSAGES_POLL_MS = 20000;
const OTP_POLL_MS = 15000;
const PAGE_SIZE_MESSAGES = 20; // igual ao da API

export function useConversations(assigneeType: AssigneeType = 'all', status = 'open') {
  return useQuery({
    queryKey: ['support', 'conversations', assigneeType, status],
    queryFn: () => supportApi.listConversations({ assignee_type: assigneeType, status }),
    refetchInterval: CONVERSATIONS_POLL_MS,
    // Socket caído + aba em segundo plano deixava o painel cego.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

// Histórico paginado. O Chatwoot devolve uma página por vez (mais antigas
// primeiro dentro da página); `before` = id da mensagem mais antiga já carregada.
// Sem isso o atendente só enxergava a última página — o começo da conversa, que
// é onde fica a nota de triagem, ficava inacessível.
export function useMessages(conversationId: number | null) {
  const query = useInfiniteQuery({
    queryKey: ['support', 'messages', conversationId],
    queryFn: ({ pageParam }) =>
      supportApi.getMessages(conversationId as number, pageParam as number | undefined),
    initialPageParam: undefined as number | undefined,
    // Página menor que o lote = chegamos no início (sem pedir uma página vazia
    // só pra descobrir isso).
    getNextPageParam: (lastPage: ChatwootMessage[]) =>
      lastPage.length >= PAGE_SIZE_MESSAGES ? lastPage[0]?.id : undefined,
    enabled: conversationId != null,
    refetchInterval: conversationId != null ? MESSAGES_POLL_MS : false,
    staleTime: 5_000,
  });

  // As páginas vêm da mais recente para a mais antiga; a UI quer o contrário.
  const messages = useMemo(() => {
    const pages = query.data?.pages ?? [];
    return [...pages].reverse().flat();
  }, [query.data]);

  return {
    messages,
    isLoading: query.isLoading,
    isError: query.isError,
    hasOlder: query.hasNextPage,
    loadOlder: query.fetchNextPage,
    isLoadingOlder: query.isFetchingNextPage,
    refetch: query.refetch,
  };
}

// Atendimentos anteriores do mesmo contato (as fatias que o Chatwoot criou
// quando a inbox ainda abria conversa nova a cada retorno do cliente).
// Fica em cache longo: histórico encerrado não muda.
export function useThreadHistory(conversationId: number | null) {
  return useQuery({
    queryKey: ['support', 'thread', conversationId],
    queryFn: () => supportApi.getThreadHistory(conversationId as number),
    enabled: !!conversationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProducer(phone: string | null | undefined) {
  return useQuery({
    queryKey: ['support', 'producer', phone],
    queryFn: () => supportApi.getProducer(phone as string),
    enabled: !!phone,
    staleTime: 60000,
  });
}

// O socket não recebe o código nem dados do produtor: ele apenas invalida o
// cache. O polling mantém a ferramenta útil caso a conexão realtime caia.
//
// SOB DEMANDA: `ativo` só fica true quando o atendente pede para ver o código.
// Antes isto buscava sozinho a cada 15s em toda conversa aberta — o código de
// acesso do produtor trafegava e ficava na memória do browser sem ninguém ter
// pedido. Agora nada sai do servidor até haver um clique, e cada leitura vira
// uma linha de auditoria que significa alguma coisa.
export function useProducerOtp(phone: string | null | undefined, ativo = false) {
  const qc = useQueryClient();
  const habilitado = !!phone && ativo;

  useEffect(() => {
    if (!habilitado) return;

    const refreshOtp = () => {
      qc.invalidateQueries({ queryKey: ['support', 'producer', 'otp', phone] });
    };
    socketClient.on('support:otp-updated', refreshOtp);
    return () => socketClient.off('support:otp-updated', refreshOtp);
  }, [habilitado, phone, qc]);

  return useQuery({
    queryKey: ['support', 'producer', 'otp', phone],
    queryFn: () => supportApi.getProducerOtp(phone as string),
    enabled: habilitado,
    staleTime: 0,
    // Só enquanto está à mostra: se o cliente pedir outro código no meio do
    // atendimento, o atendente vê o novo sem precisar clicar de novo.
    refetchInterval: habilitado ? OTP_POLL_MS : false,
    refetchOnWindowFocus: habilitado,
  });
}

function invalidarConversa(qc: ReturnType<typeof useQueryClient>, convId: number) {
  qc.invalidateQueries({ queryKey: ['support', 'messages', convId] });
  qc.invalidateQueries({ queryKey: ['support', 'conversations'] });
}

export interface EnvioTexto {
  convId: number;
  content: string;
  isPrivate?: boolean;
  clientMessageId: string;
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ convId, content, isPrivate, clientMessageId }: EnvioTexto) =>
      supportApi.sendMessage(convId, content, isPrivate, clientMessageId),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

// Envio de áudio gravado. Mesma invalidação do texto — a mensagem entra na
// timeline com o anexo já processado pela API.
export function useSendAudio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      convId,
      blob,
      filename,
      clientMessageId,
    }: {
      convId: number;
      blob: Blob;
      filename: string;
      clientMessageId: string;
    }) => supportApi.sendAudio(convId, blob, filename, clientMessageId),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

// Envio de imagem, vídeo ou documento.
export function useSendAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      convId,
      file,
      caption,
      clientMessageId,
      trecho,
    }: {
      convId: number;
      file: File;
      caption?: string;
      clientMessageId: string;
      trecho?: { inicio: number; fim: number } | null;
    }) => supportApi.sendAttachment(convId, file, caption, clientMessageId, trecho),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

// Sinaliza "digitando" ao cliente no WhatsApp (e marca as mensagens dele como
// lidas). O indicador da Meta dura 25s ou some quando respondemos, então
// reenviar antes disso mantém a bolinha viva sem spammar a API.
const TYPING_INTERVALO_MS = 18000;

export function useTypingSignal(conversationId: number | null) {
  const ultimoEnvio = useRef(0);

  useEffect(() => {
    ultimoEnvio.current = 0; // trocou de conversa, pode sinalizar de novo
  }, [conversationId]);

  return useCallback(() => {
    if (!conversationId) return;
    const agora = Date.now();
    if (agora - ultimoEnvio.current < TYPING_INTERVALO_MS) return;
    ultimoEnvio.current = agora;
    void supportApi.setTyping(conversationId, true);
  }, [conversationId]);
}

type ConvStatus = 'open' | 'resolved' | 'pending' | 'snoozed';

// Resolver/reabrir/pausar a conversa.
export function useToggleStatus() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { convId: number; status: ConvStatus }>({
    mutationFn: ({ convId, status }) => supportApi.toggleStatus(convId, status),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

// Assumir (atribuir a mim) ou largar (unassign) a conversa.
export function useAssign() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { convId: number; unassign: boolean }>({
    mutationFn: ({ convId, unassign }) => supportApi.assign(convId, unassign),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

// Atendentes disponíveis (pra transferência). Cache folgado — muda pouco.
export function useAgents() {
  return useQuery({
    queryKey: ['support', 'agents'],
    queryFn: () => supportApi.listAgents(),
    staleTime: 5 * 60 * 1000,
  });
}

// Etiquetas (flags) da conta.
export function useLabels() {
  return useQuery({
    queryKey: ['support', 'labels'],
    queryFn: () => supportApi.listLabels(),
    staleTime: 5 * 60 * 1000,
  });
}

// Respostas rápidas.
export function useCanned() {
  return useQuery({
    queryKey: ['support', 'canned'],
    queryFn: () => supportApi.listCanned(),
    staleTime: 5 * 60 * 1000,
  });
}

// Transferir a conversa pra outro atendente.
export function useTransfer() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { convId: number; assigneeId: number }>({
    mutationFn: ({ convId, assigneeId }) => supportApi.transfer(convId, assigneeId),
    onSettled: (_d, _e, v) => invalidarConversa(qc, v.convId),
  });
}

type ListaDeConversas = { meta: unknown; conversations: Conversation[] };

// Etiquetas: atualiza o cache na hora (otimista). Dois cliques rápidos antes
// calculavam a lista nova a partir da antiga e o segundo apagava o primeiro.
export function useSetLabels() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { convId: number; labels: string[] },
    { anteriores: [readonly unknown[], ListaDeConversas | undefined][] }
  >({
    mutationFn: ({ convId, labels }) => supportApi.setLabels(convId, labels),
    onMutate: async ({ convId, labels }) => {
      await qc.cancelQueries({ queryKey: ['support', 'conversations'] });
      const anteriores = qc.getQueriesData<ListaDeConversas>({
        queryKey: ['support', 'conversations'],
      });
      for (const [key, data] of anteriores) {
        if (!data?.conversations) continue;
        qc.setQueryData<ListaDeConversas>(key, {
          ...data,
          conversations: data.conversations.map((c) => (c.id === convId ? { ...c, labels } : c)),
        });
      }
      return { anteriores };
    },
    onError: (_e, _v, ctx) => {
      for (const [key, data] of ctx?.anteriores ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['support', 'conversations'] }),
  });
}

// Aceita uma transferência (destinatário clica "Assumir" no modal central).
export function useAcceptTransfer() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: (conversationId: number) => supportApi.acceptTransfer(conversationId),
    onSettled: (_d, _e, conversationId) => invalidarConversa(qc, conversationId),
  });
}

// Marca uma conversa como lida (zera o unread_count na listagem). Recebe o id
// como argumento da mutation pra poder ser chamada ao abrir qualquer conversa.
export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, number>({
    mutationFn: (conversationId: number) => supportApi.markRead(conversationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support', 'conversations'] }),
  });
}

// Fixar/desfixar a conversa.
export function usePin() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { convId: number; pinned: boolean }>({
    mutationFn: ({ convId, pinned }) => supportApi.pin(convId, pinned),
    onSettled: () => qc.invalidateQueries({ queryKey: ['support', 'conversations'] }),
  });
}

// Corrige ortografia/gramática do rascunho. Não toca em nenhum cache — não
// muda dado nenhum, só devolve o texto corrigido pro atendente revisar.
export function useCorrectDraft() {
  return useMutation<string, Error, string>({
    mutationFn: (draft: string) => supportApi.correctDraft(draft),
  });
}

// Sugere a próxima resposta com base no histórico da conversa.
export function useSuggestReply() {
  return useMutation<string, Error, number>({
    mutationFn: (convId: number) => supportApi.suggestReply(convId),
  });
}
